import BackButton from '../navigation/BackButton'
import { getCurrentUser } from '../../lib/auth'
import { useLocation, useNavigate } from 'react-router-dom'
import { useNavigationParams } from '../navigation/history'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { apiFetch, ApiError } from '../../lib/api'
import { useAuthSession } from '../../lib/authSession'
import { readerPage, type OcrGeometry } from '../../lib/documentReader'
import {
  humanizeToken,
  formatOfficerDate,
  formatOfficerDateTime,
  formatOfficerValue,
} from '../../lib/officerFormat'
import { useTranslationEnabled } from '../translate/api'
import DocumentPdf, { type SourceAnnotation } from './DocumentPdf'
import PaymentObservationsCard from './PaymentObservationsCard'
import type { DocumentMetadata } from './DocumentLink'
import './reader.css'

interface Sources {
  document_id: string
  context: string | null
  context_title?: string | null
  annotations: SourceAnnotation[]
}
interface PageText {
  document_id: string
  page: number
  source_hash: string
  status: 'text' | 'no_text'
  original_source_hash?: string | null
  text_source?: 'native' | 'ocr'
  has_images?: boolean
  ocr?: OcrGeometry | null
  blocks: { id: string; text: string }[]
}
interface English {
  document_id: string
  page: number
  source_hash: string
  status: 'translated' | 'no_text'
  original_source_hash?: string
  text_source?: 'native' | 'ocr'
  ocr?: OcrGeometry | null
  blocks: {
    id: string
    original: string
    translated: string
    completion: 'reported' | 'not_reported' | 'mock'
  }[]
}
function failure(error: unknown): string {
  if (
    error instanceof ApiError &&
    error.body &&
    typeof error.body === 'object' &&
    'detail' in error.body
  )
    return typeof (error.body as { detail: unknown }).detail === 'string'
      ? String((error.body as { detail: unknown }).detail)
      : error.message
  return error instanceof Error ? error.message : 'The document could not be read'
}
const FINANCIAL_FIELDS = new Set([
  'units_held',
  'outstanding_units',
  'units_delta',
  'amount',
  'price',
  'price_per_unit',
  'cost',
  'total_cost',
  'nominal_value',
  'commitment',
  'drawn',
  'undrawn',
])
function fieldLabel(field: string): string {
  return (
    (
      { units_held: 'Position balance', outstanding_units: 'Issued amount' } as Record<
        string,
        string
      >
    )[field] ?? humanizeToken(field)
  )
}
function valueText(value: unknown, field = ''): string {
  if (value === null || value === undefined) return 'Not captured'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object')
    return Array.isArray(value)
      ? value.map((entry) => valueText(entry, field)).join(' · ')
      : Object.entries(value)
          .map(([key, entry]) => `${fieldLabel(key)}: ${valueText(entry, key)}`)
          .join(' · ')
  if (typeof value === 'number' || FINANCIAL_FIELDS.has(field)) return formatOfficerValue(value)
  if (
    typeof value === 'string' &&
    (/(?:_date|_dates)$/.test(field) || ['as_of', 'valid_from', 'valid_to'].includes(field))
  )
    return formatOfficerDate(value) ?? value
  return String(value)
}

export default function DocumentReader() {
  const { id = '' } = useParams<{ id: string }>()
  const [params, setParams] = useNavigationParams()
  const session = useAuthSession()
  const requestKey = `${id}:${session ?? ''}`
  const [metadata, setMetadata] = useState<{ key: string; value: DocumentMetadata } | null>(null)
  const [sourceState, setSourceState] = useState<{ key: string; value: Sources } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sourceError, setSourceError] = useState<string | null>(null)
  const [numPages, setNumPages] = useState(0)
  const [zoom, setZoom] = useState(1)
  const location = useLocation()
  const navigate = useNavigate()
  const actor = getCurrentUser()
  const readerOwner = actor ? actor.sub + ':' + actor.depositary_id : null
  const savedMode = location.state?.nolosReaderMode
  const storedMode: 'original' | 'english' =
    readerOwner &&
    savedMode?.owner === readerOwner &&
    savedMode?.documentId === id &&
    savedMode?.mode === 'english'
      ? 'english'
      : 'original'
  // Presentation choice belongs to this account's history entry, never a
  // bookmark that could request a translation without an earlier choice.
  const readerState = (next: 'original' | 'english') => ({
    ...location.state,
    nolosReaderMode: { documentId: id, owner: readerOwner, mode: next },
  })
  const setMode = (next: 'original' | 'english') => {
    if (next === storedMode) return
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: readerState(next) }
    )
  }
  const [english, setEnglish] = useState<{ key: string; value: English } | null>(null)
  const [englishError, setEnglishError] = useState<string | null>(null)
  const [loadingEnglish, setLoadingEnglish] = useState(false)
  const [ocrState, setOcrState] = useState<{ key: string; value: PageText } | null>(null)
  const [ocrErrorState, setOcrError] = useState<{ key: string; message: string } | null>(null)
  const ocrRequests = useRef(new Map<string, AbortController>())
  const [retry, setRetry] = useState(0)
  const [expanded, setExpanded] = useState(false)
  const root = useRef<HTMLElement>(null)
  const translationEnabled = useTranslationEnabled()
  const selected = params.get('source')
  const fund = params.get('fund')
  const focus = params.get('focus')
  const proposal = params.get('proposal')
  const sourceKey = `${requestKey}:${fund}:${focus}:${proposal}`
  const sources = sourceState?.key === sourceKey ? sourceState.value : null
  const doc = metadata?.key === requestKey ? metadata.value : null
  const mode =
    storedMode === 'english' &&
    (translationEnabled === true || doc?.source_language?.toLowerCase() === 'en')
      ? 'english'
      : 'original'
  const firstPage = doc?.source_page_start ?? 1
  const lastPage = doc?.source_page_end ?? (numPages || doc?.page_count || 0)
  const page = readerPage(params.get('page')) ?? firstPage
  const outsideSource = page < firstPage || (lastPage > 0 && page > lastPage)
  const englishKey = `${requestKey}:${page}`
  const currentEnglish = english?.key === englishKey ? english.value : null
  const currentOcr = ocrState?.key === englishKey ? ocrState.value : null
  const ocrError = ocrErrorState?.key === englishKey ? ocrErrorState.message : null
  const requestOcr = useCallback(() => {
    if (ocrRequests.current.has(englishKey)) return
    const controller = new AbortController()
    ocrRequests.current.set(englishKey, controller)
    setOcrError(null)
    apiFetch<PageText>(`/api/documents/${encodeURIComponent(id)}/reader/pages/${page}`, {
      signal: controller.signal,
    })
      .then(async (original) => {
        const value = await apiFetch<PageText>(
          `/api/documents/${encodeURIComponent(id)}/reader/pages/${page}/transcription?source_hash=${original.source_hash}`,
          { method: 'POST', signal: controller.signal }
        )
        if (value.original_source_hash !== original.source_hash)
          throw new Error('Transcription did not match the original page')
        return value
      })
      .then((value) => {
        if (value.document_id !== id || value.page !== page || value.text_source !== 'ocr')
          throw new Error('Transcription did not match the requested page')
        if (!controller.signal.aborted) setOcrState({ key: englishKey, value })
      })
      .catch((error) => {
        if (!controller.signal.aborted) setOcrError({ key: englishKey, message: failure(error) })
      })
      .finally(() => {
        // Only in-flight work is deduplicated. The visible OCR state holds one
        // page; visiting another page must not suppress a later return to it.
        if (ocrRequests.current.get(englishKey) === controller)
          ocrRequests.current.delete(englishKey)
      })
  }, [id, page, englishKey])
  useEffect(() => {
    const requests = ocrRequests.current
    return () => {
      for (const request of requests.values()) request.abort()
      requests.clear()
    }
  }, [requestKey])
  const returnPath = params.get('return')
  const returnTab = ['record', 'evidence', 'overview'].includes(params.get('tab') ?? '')
    ? params.get('tab')
    : 'record'
  const returnTo =
    returnPath && /^\/v2(?:\/|\?|$)/.test(returnPath) && !returnPath.includes('\\')
      ? returnPath
      : fund
        ? `/v2/funds/${encodeURIComponent(fund)}?tab=${returnTab}${focus ? `&focus=${encodeURIComponent(focus)}` : ''}`
        : '/v2/traces'

  useEffect(() => {
    const controller = new AbortController()
    setMetadata(null)
    setSourceState(null)
    setError(null)
    setSourceError(null)
    setEnglish(null)
    setOcrState(null)
    setOcrError(null)
    apiFetch<DocumentMetadata>(`/api/documents/${encodeURIComponent(id)}`, {
      signal: controller.signal,
    })
      .then((value) => {
        if (!controller.signal.aborted) setMetadata({ key: requestKey, value })
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(failure(e))
      })
    const query = new URLSearchParams()
    if (fund) query.set('fund', fund)
    if (focus) query.set('focus', focus)
    if (proposal) query.set('proposal', proposal)
    apiFetch<Sources>(`/api/documents/${encodeURIComponent(id)}/reader?${query}`, {
      signal: controller.signal,
    })
      .then((value) => {
        if (!controller.signal.aborted) setSourceState({ key: sourceKey, value })
      })
      .catch((e) => {
        if (!controller.signal.aborted) setSourceError(failure(e))
      })
    return () => controller.abort()
  }, [id, requestKey, fund, focus, proposal, sourceKey])

  useEffect(() => {
    if (mode !== 'english' || !doc || outsideSource) return
    const controller = new AbortController()
    setLoadingEnglish(true)
    setEnglishError(null)
    setEnglish(null)
    apiFetch<PageText>(`/api/documents/${encodeURIComponent(id)}/reader/pages/${page}`, {
      signal: controller.signal,
    })
      .then(async (original): Promise<English> => {
        // A document-level language hint cannot prove every physical page is
        // English (mixed-language statements are common). The on-demand page
        // endpoint binds translation to this exact page, including its images.
        const translated = await apiFetch<English>(
          `/api/documents/${encodeURIComponent(id)}/reader/pages/${page}/english?source_hash=${original.source_hash}`,
          { method: 'POST', signal: controller.signal }
        )
        if (
          translated.document_id !== id ||
          translated.page !== page ||
          (translated.original_source_hash ?? translated.source_hash) !== original.source_hash
        )
          throw new Error('English did not match the requested original page')
        return translated
      })
      .then((value) => {
        if (!controller.signal.aborted) setEnglish({ key: englishKey, value })
      })
      .catch((e) => {
        if (!controller.signal.aborted) setEnglishError(failure(e))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingEnglish(false)
      })
    return () => controller.abort()
  }, [mode, doc, id, page, englishKey, retry, outsideSource])

  const navigatePage = (next: number, readingMode: 'original' | 'english' = mode) => {
    if (next < firstPage || (lastPage > 0 && next > lastPage)) return
    if (next === page && !selected) {
      setMode(readingMode)
      return
    }
    setParams(
      (old) => {
        const nextParams = new URLSearchParams(old)
        nextParams.set('page', String(next))
        nextParams.delete('source')
        return nextParams
      },
      { replace: false, state: readerState(readingMode) }
    )
  }
  const selectSource = (sourceId: string) => {
    const annotation = sources?.annotations.find((row) => row.id === sourceId)
    if (!annotation) return
    if (selected === sourceId && (annotation.page ?? page) === page) {
      setMode('original')
      return
    }
    setParams(
      (old) => {
        const next = new URLSearchParams(old)
        next.set('source', sourceId)
        if (annotation.page) next.set('page', String(annotation.page))
        return next
      },
      { replace: false, state: readerState('original') }
    )
  }
  const selectedFact = sources?.annotations.find((row) => row.id === selected)
  useEffect(() => {
    const changed = () => setExpanded(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', changed)
    return () => document.removeEventListener('fullscreenchange', changed)
  }, [])
  const fullscreen = () => {
    setExpanded((value) => !value)
    if (!document.fullscreenElement) root.current?.requestFullscreen?.().catch(() => {})
    else document.exitFullscreen?.().catch(() => {})
  }

  return (
    <main
      ref={root}
      className={`document-reader${expanded ? ' expanded' : ''}`}
      data-testid="document-reader"
    >
      <header className="document-reader-head">
        <div>
          <span className="document-brand">NOLOS</span>
          <h1>{doc?.display_title ?? (error ? 'Document unavailable' : 'Document')}</h1>
          {sources?.context_title ? <p>{sources.context_title}</p> : null}
        </div>
        <div className="document-reader-actions">
          <BackButton fallback={returnTo} />
          <Link to={returnTo}>Return to context</Link>
          <Link to={`/v2/documents/${encodeURIComponent(id)}/details`}>Details & history</Link>
          <button onClick={() => window.close()} type="button">
            Close window
          </button>
        </div>
      </header>
      {error ? (
        <p className="reader-error" role="alert">
          {error}
        </p>
      ) : !doc ? (
        <p className="reader-note" role="status">
          Loading document…
        </p>
      ) : (
        <>
          <div className="document-reader-toolbar">
            <div className="reader-mode">
              <button
                type="button"
                aria-pressed={mode === 'original'}
                onClick={() => setMode('original')}
              >
                Original
              </button>
              <button
                type="button"
                aria-pressed={mode === 'english'}
                disabled={
                  translationEnabled !== true && doc.source_language?.toLowerCase() !== 'en'
                }
                title={translationEnabled === false ? 'English reading is not enabled' : undefined}
                onClick={() => setMode('english')}
              >
                English
              </button>
            </div>
            <div className="reader-pages">
              <button
                type="button"
                aria-label="Previous page"
                disabled={page <= firstPage}
                onClick={() => navigatePage(page - 1)}
              >
                ‹
              </button>
              <span>
                Page {outsideSource ? '—' : page - firstPage + 1} /{' '}
                {lastPage ? lastPage - firstPage + 1 : '…'}
              </span>
              <button
                type="button"
                aria-label="Next page"
                disabled={lastPage === 0 || page >= lastPage}
                onClick={() => navigatePage(page + 1)}
              >
                ›
              </button>
            </div>
            <div className="reader-scale">
              <button
                type="button"
                onClick={() => setZoom((v) => Math.max(0.5, v - 0.2))}
                aria-label="Zoom out"
              >
                −
              </button>
              <button type="button" onClick={() => setZoom(1)}>
                Fit width
              </button>
              <button
                type="button"
                onClick={() => setZoom((v) => Math.min(2.5, v + 0.2))}
                aria-label="Zoom in"
              >
                +
              </button>
              <button type="button" onClick={fullscreen}>
                {expanded ? 'Exit fullscreen' : 'Fullscreen'}
              </button>
            </div>
          </div>
          <div className="document-reader-body">
            <section
              className="document-reader-original"
              aria-label={mode === 'original' ? 'Original document' : 'English reading'}
            >
              {outsideSource ? (
                <div className="reader-error" role="alert">
                  Page is outside this document’s source range.{' '}
                  <button type="button" onClick={() => navigatePage(firstPage)}>
                    Open first page
                  </button>
                </div>
              ) : null}
              <div hidden={mode !== 'original'} className="document-original-container">
                {!outsideSource ? (
                  <DocumentPdf
                    documentId={id}
                    session={session}
                    page={page}
                    zoom={zoom}
                    annotations={sources?.annotations ?? []}
                    selected={selected}
                    onSelect={selectSource}
                    onPages={setNumPages}
                    ocr={currentOcr?.ocr ?? currentEnglish?.ocr}
                    onNeedsOcr={requestOcr}
                  />
                ) : null}
              </div>
              {mode === 'english' && !outsideSource ? (
                <div className="document-english" data-testid="document-english">
                  {loadingEnglish ? <p role="status">Preparing English for page {page}…</p> : null}
                  {englishError ? (
                    <div className="reader-error" role="alert">
                      {englishError}{' '}
                      <button onClick={() => setRetry((n) => n + 1)}>Try again</button>
                    </div>
                  ) : null}
                  {currentEnglish?.status === 'no_text' ? (
                    <p className="reader-note">
                      No readable text was found on this page. The original image remains available;
                      English and exact text highlights could not be prepared.
                    </p>
                  ) : null}
                  {currentEnglish?.text_source === 'ocr' ? (
                    <p className="reader-note">
                      Transcribed from the original image. Compare uncertain words and figures with
                      the original.
                    </p>
                  ) : null}
                  {currentEnglish?.blocks.some((block) => block.completion === 'not_reported') ? (
                    <p className="reader-note">
                      Machine English — compare with the original. Complete output was not
                      independently confirmed.
                    </p>
                  ) : null}
                  {currentEnglish?.blocks.map((block) => (
                    <article key={block.id} className="document-english-block">
                      <p>{block.translated}</p>
                      <details>
                        <summary>Original text · page {page}</summary>
                        <p>{block.original}</p>
                        <button type="button" onClick={() => setMode('original')}>
                          Open original page
                        </button>
                      </details>
                    </article>
                  ))}
                </div>
              ) : null}
            </section>
            <aside className="document-reader-sources" aria-label="Captured source facts">
              <h2>{focus || proposal ? 'Sources in this context' : 'Captured document facts'}</h2>
              {ocrError ? (
                <p className="reader-note" role="status">
                  {ocrError}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      ocrRequests.current.delete(englishKey)
                      requestOcr()
                    }}
                  >
                    Try scan reading again
                  </button>
                </p>
              ) : null}
              <p className="reader-note">
                Select a field to open its saved passage. Source capture and evidence approval are
                separate.
              </p>
              {sourceError ? (
                <p className="reader-error">{sourceError}</p>
              ) : sources && !sources.annotations.length ? (
                <p className="reader-note">
                  No source passages are captured for this context. The original remains available.
                </p>
              ) : null}
              {sources?.annotations.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className={`reader-source-fact${selected === row.id ? ' selected' : ''}`}
                  data-testid="reader-source-fact"
                  onClick={() => selectSource(row.id)}
                >
                  <span>{fieldLabel(row.field)}</span>
                  <b>{valueText(row.value, row.field)}</b>
                  {row.source?.entity_name ? (
                    <small>Source subject: {row.source.entity_name}</small>
                  ) : null}
                  {row.proposal_value_changed ? (
                    <small>Proposal value: {valueText(row.proposal_value, row.field)}</small>
                  ) : null}
                  <small>
                    {row.origin === 'proposal'
                      ? 'Captured proposal'
                      : row.origin === 'evidence'
                        ? 'Evidence reading'
                        : 'Captured source'}
                    {row.status === 'referenced' ? '' : ` · ${humanizeToken(row.status)}`}
                    {row.page ? ` · p. ${row.page}` : ' · no page captured'}
                  </small>
                </button>
              ))}
              {selectedFact && selectedFact.location_status !== 'captured' ? (
                <div className="reader-note" role="status">
                  {selectedFact.location_status === 'page_only'
                    ? 'Only a page was captured. No exact passage has been guessed.'
                    : 'No valid source passage was captured for this field.'}{' '}
                  <Link to={`/v2/documents/${encodeURIComponent(id)}/details`}>
                    Open source history
                  </Link>
                </div>
              ) : null}
              <PaymentObservationsCard
                documentId={id}
                onPage={(next) => navigatePage(next, 'original')}
              />
              <details className="reader-document-details">
                <summary>Document details</summary>
                <dl>
                  <dt>Original filename</dt>
                  <dd>{doc.original_filename}</dd>
                  <dt>Document date</dt>
                  <dd>
                    {doc.document_date ? formatOfficerDate(doc.document_date) : 'Not captured'}
                  </dd>
                  <dt>Received</dt>
                  <dd>{formatOfficerDateTime(doc.created_at)}</dd>
                  <dt>Status</dt>
                  <dd>{humanizeToken(doc.status)}</dd>
                </dl>
              </details>
            </aside>
          </div>
        </>
      )}
    </main>
  )
}
